import React, { useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ExternalLink, Shield, Lock, ToggleRight, Users } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import LaunchChecklistItem from "./LaunchChecklistItem";

const TUTORIAL_KEY = "launch-checklist";

const ITEMS = [
    { key: "launch-add-domain", icon: Globe, action: "panel:domains" },
    { key: "launch-test-app", icon: ExternalLink, action: "openApp" },
    { key: "launch-data-security", icon: Shield, action: "panel:data-security" },
    { key: "launch-integration-security", icon: Lock, action: "panel:integrations" },
    { key: "launch-turn-off-public", icon: ToggleRight, action: "panel:integrations" },
    { key: "launch-invite-testers", icon: Users, action: "panel:users" },
];

export default function LaunchChecklist({ onSwitchPanel, onOpenApp }) {
    const { t } = useTranslation();
    const { user, getUser } = useAuth();
    const { call: markComplete } = useBluelagoon({ method: "markTutorialComplete" });
    const savingRef = useRef(false);

    const checklist = user?.tutorials?.[TUTORIAL_KEY]?.metadata || {};
    const completedCount = useMemo(() => ITEMS.filter((i) => checklist[i.key]).length, [checklist]);

    const handleToggle = useCallback(async (key) => {
        if (savingRef.current) return;
        savingRef.current = true;
        try {
            const updated = { ...checklist, [key]: !checklist[key] };
            await markComplete({ tutorialKey: TUTORIAL_KEY, metadata: updated });
            await getUser();
            const newCount = ITEMS.filter((i) => updated[i.key]).length;
            if (newCount === ITEMS.length) {
                toast.success(t("launchChecklist.allComplete"));
            }
        } finally {
            savingRef.current = false;
        }
    }, [checklist, markComplete, getUser, t]);

    const handleAction = useCallback((action) => {
        if (action === "openApp") return onOpenApp?.();
        if (action.startsWith("panel:")) return onSwitchPanel?.(action.replace("panel:", ""));
    }, [onSwitchPanel, onOpenApp]);

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
                {t("launchChecklist.title")}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
                {t("launchChecklist.subtitle")}
            </p>
            <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>{t("launchChecklist.progress", { completed: completedCount, total: ITEMS.length })}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(completedCount / ITEMS.length) * 100}%` }}
                    />
                </div>
            </div>
            <div className="space-y-2">
                {ITEMS.map((item) => (
                    <LaunchChecklistItem
                        key={item.key}
                        title={t(`launchChecklist.items.${item.key}`)}
                        checked={!!checklist[item.key]}
                        onToggle={() => handleToggle(item.key)}
                        onAction={() => handleAction(item.action)}
                        icon={item.icon}
                    />
                ))}
            </div>
        </div>
    );
}
